import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ManaText } from "@/components/common/ManaSymbol";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AuthorityBadge } from "@/components/rules/RulingsPanel";

function Row({ kind, item, onDone }) {
  const id = item.mechanic_id || item.ruling_id;
  const act = async (decision) => {
    const note = decision === "reject" ? window.prompt("Reason (shared with the author):", "") : "";
    if (note === null) return;
    try {
      const r = await api.post(`/rules/${kind}/${id}/review`, { decision, note: note || "" });
      toast.success(r.data.review_stage === "second" ? "First review recorded: a different reviewer makes the second review" : `Marked ${r.data.status}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <li className="panel p-4" data-testid={`rules-queue-item-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {kind === "ruling" ? <AuthorityBadge authority={item.authority} /> : <span className="font-heading text-amber-100">{item.name}</span>}
        <span className="font-mono text-[10px] uppercase text-slate-500">{item.review_stage} review · by @{(item.creator || item.author)?.username}</span>
        {item.first_review && <span className="font-mono text-[10px] text-emerald-300">1st: {item.first_review.decision} by @{item.first_review.user?.username}</span>}
      </div>
      <p className="mt-2 whitespace-pre-line text-sm text-slate-300"><ManaText text={item.definition || item.text} /></p>
      {kind === "ruling" && <p className="mt-1 font-mono text-[10px] text-slate-500">on {item.target.identity}</p>}
      <div className="mt-3 flex gap-2">
        <button className="btn-gold text-xs" onClick={() => act("approve")} data-testid={`rules-approve-${id}`}>Approve</button>
        <button className="btn-outline text-xs" onClick={() => act("reject")} data-testid={`rules-reject-${id}`}>Reject</button>
      </div>
    </li>
  );
}

export function RulesQueue({ communityId }) {
  const qc = useQueryClient();
  const key = ["rules-queue", communityId || "platform"];
  const { data, error } = useQuery({ queryKey: key, retry: false, queryFn: () => api.get("/rules/queue", { params: { community_id: communityId } }).then((r) => r.data) });
  if (error) return null;
  const done = () => qc.invalidateQueries({ queryKey: key });
  const empty = data && !data.mechanics.length && !data.rulings.length;
  return (
    <div className="space-y-4" data-testid={communityId ? "community-rules-queue" : "staff-rules-queue"}>
      <p className="text-sm text-slate-500">{communityId ? "Community-scoped mechanics awaiting your approval." : "Platform-wide mechanics and Foundry rulings need two independent Rules Reviewers. You cannot review your own work or both stages."}</p>
      <ul className="space-y-3">
        {data?.mechanics.map((m) => <Row key={m.mechanic_id} kind="mechanic" item={m} onDone={done} />)}
        {data?.rulings.map((r) => <Row key={r.ruling_id} kind="ruling" item={r} onDone={done} />)}
      </ul>
      {empty && <p className="text-sm text-slate-500" data-testid="rules-queue-empty">Nothing awaiting review.</p>}
    </div>
  );
}
